import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";

// Before/After image comparison slider — single-scroller variant.
// Adapted from codepen.io/nosurprisethere/pen/JJzdoP with react state,
// mouse + touch support, keyboard accessibility, and a themed scrubber.

export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeLabel = "Before",
  afterLabel = "After",
  initial = 50,
  aspectClassName = "aspect-[4/5] md:aspect-square",
}) {
  const wrapperRef = useRef(null);
  const [pos, setPos] = useState(initial); // percent 0..100
  const [dragging, setDragging] = useState(false);

  const setPositionFromClientX = useCallback((clientX) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setPositionFromClientX(x);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, setPositionFromClientX]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 3));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 3));
  };

  return (
    <div
      ref={wrapperRef}
      data-testid="before-after-slider"
      className={`relative w-full ${aspectClassName} overflow-hidden select-none bg-[#0F2A44]`}
      style={{ boxShadow: "0 20px 40px rgba(15,42,68,0.24)" }}
    >
      {/* Bottom (Before) — full image */}
      <img
        src={beforeSrc}
        alt="Before cladding"
        draggable={false}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />

      {/* Top (After) — clipped width */}
      <div
        className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={afterSrc}
          alt="After cladding"
          draggable={false}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            width: wrapperRef.current
              ? `${wrapperRef.current.offsetWidth}px`
              : "100%",
            maxWidth: "none",
          }}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 z-10 px-3 py-1.5 bg-[#189A96] text-white text-[0.68rem] font-sans font-semibold tracking-[0.14em] uppercase">
        {afterLabel}
      </div>
      <div className="absolute top-4 right-4 z-10 px-3 py-1.5 bg-white/90 text-[#0F2A44] text-[0.68rem] font-sans font-semibold tracking-[0.14em] uppercase">
        {beforeLabel}
      </div>

      {/* Vertical divider line + scrubber */}
      <div
        className="absolute inset-y-0 z-20 pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-[3px] h-full bg-white shadow-[0_0_20px_rgba(0,0,0,0.35)]" />
      </div>

      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onKeyDown={onKey}
        data-testid="ba-scrubber"
        aria-label="Drag to reveal after / before"
        className={`absolute top-1/2 -translate-y-1/2 z-30 w-14 h-14 rounded-full bg-white flex items-center justify-center transition-all duration-150 ease-out ${
          dragging ? "scale-110 shadow-xl" : "shadow-lg hover:scale-105"
        }`}
        style={{
          left: `${pos}%`,
          transform: "translate(-50%, -50%)",
          cursor: dragging ? "grabbing" : "grab",
          border: "3px solid #189A96",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 100 100"
          fill="none"
        >
          <polygon points="0 50 37 68 37 32" fill="#189A96" />
          <polygon points="100 50 63 32 63 68" fill="#189A96" />
        </svg>
      </button>

      {/* Bottom hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 px-3 py-1.5 bg-black/40 backdrop-blur-sm text-white text-[0.65rem] font-sans tracking-[0.14em] uppercase">
        Drag to compare
      </div>
    </div>
  );
}

// Three-image overlap slider — a wordless, full-bleed variant.
// Layers stack left-to-right: image[0] is the base, image[1] is clipped to
// reveal from handle 1 rightward, image[2] is clipped to reveal from handle
// 2 rightward (on top of both). Two draggable handles, no labels/text.
export function TripleImageSlider({ images, alts = [], className = "" }) {
  const wrapperRef = useRef(null);
  const [pos1, setPos1] = useState(33);
  const [pos2, setPos2] = useState(66);
  const dragHandle = useRef(null); // 1 | 2 | null
  const [, forceRender] = useState(0);
  const [interacted, setInteracted] = useState(false);

  const setFromClientX = useCallback((clientX, handle) => {
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    const clamped = Math.max(0, Math.min(100, pct));
    if (handle === 1) {
      setPos1(Math.min(clamped, pos2 - 2));
    } else {
      setPos2(Math.max(clamped, pos1 + 2));
    }
  }, [pos1, pos2]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragHandle.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x, dragHandle.current);
    };
    const onUp = () => {
      dragHandle.current = null;
      forceRender((n) => n + 1);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [setFromClientX]);

  const startDrag = (handle) => (e) => {
    e.preventDefault();
    dragHandle.current = handle;
    setInteracted(true);
    forceRender((n) => n + 1);
  };

  const onKey = (handle) => (e) => {
    if (e.key === "ArrowLeft") {
      if (handle === 1) setPos1((p) => Math.max(0, p - 3));
      else setPos2((p) => Math.max(pos1 + 2, p - 3));
    }
    if (e.key === "ArrowRight") {
      if (handle === 1) setPos1((p) => Math.min(pos2 - 2, p + 3));
      else setPos2((p) => Math.min(100, p + 3));
    }
  };

  const width = wrapperRef.current ? wrapperRef.current.offsetWidth : 0;
  const dragging = dragHandle.current !== null;

  return (
    <div
      ref={wrapperRef}
      data-testid="triple-image-slider"
      className={`relative w-full h-full overflow-hidden select-none bg-[#0F2A44] ${className}`}
    >
      {/* Base layer — image 1, full */}
      <img
        src={images[0]}
        alt={alts[0] || ""}
        draggable={false}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />

      {/* Middle layer — image 2, revealed from pos1 rightward */}
      <div
        className="absolute inset-y-0 right-0 overflow-hidden pointer-events-none"
        style={{ left: `${pos1}%` }}
      >
        <img
          src={images[1]}
          alt={alts[1] || ""}
          draggable={false}
          loading="lazy"
          className="absolute inset-0 h-full object-cover"
          style={{ width: width ? `${width}px` : "100%", left: `${-pos1 * width / 100}px`, maxWidth: "none" }}
        />
      </div>

      {/* Top layer — image 3, revealed from pos2 rightward */}
      <div
        className="absolute inset-y-0 right-0 overflow-hidden pointer-events-none"
        style={{ left: `${pos2}%` }}
      >
        <img
          src={images[2]}
          alt={alts[2] || ""}
          draggable={false}
          loading="lazy"
          className="absolute inset-0 h-full object-cover"
          style={{ width: width ? `${width}px` : "100%", left: `${-pos2 * width / 100}px`, maxWidth: "none" }}
        />
      </div>

      {/* Divider 1 + handle */}
      <div
        className="absolute inset-y-0 z-20 pointer-events-none"
        style={{ left: `${pos1}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-[3px] h-full bg-white shadow-[0_0_20px_rgba(0,0,0,0.35)]" />
      </div>
      <motion.button
        type="button"
        onMouseDown={startDrag(1)}
        onTouchStart={startDrag(1)}
        onKeyDown={onKey(1)}
        data-testid="triple-scrubber-1"
        aria-label="Drag to compare"
        animate={
          !interacted && dragHandle.current !== 1
            ? { scale: [1, 1.1, 1] }
            : { scale: 1 }
        }
        transition={{ duration: 1.4, repeat: !interacted ? Infinity : 0, ease: "easeInOut" }}
        className={`absolute top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center ${
          dragHandle.current === 1 ? "shadow-xl" : "shadow-lg hover:scale-105"
        }`}
        style={{
          left: `${pos1}%`,
          x: "-50%",
          y: "-50%",
          cursor: dragging ? "grabbing" : "grab",
          border: "3px solid #189A96",
        }}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 100 100"
          fill="none"
          animate={interacted ? { x: 0 } : { x: [0, -5, 0, 5, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <polygon points="0 50 37 68 37 32" fill="#189A96" />
          <polygon points="100 50 63 32 63 68" fill="#189A96" />
        </motion.svg>
      </motion.button>

      {/* Divider 2 + handle */}
      <div
        className="absolute inset-y-0 z-20 pointer-events-none"
        style={{ left: `${pos2}%`, transform: "translateX(-50%)" }}
      >
        <div className="w-[3px] h-full bg-white shadow-[0_0_20px_rgba(0,0,0,0.35)]" />
      </div>
      <motion.button
        type="button"
        onMouseDown={startDrag(2)}
        onTouchStart={startDrag(2)}
        onKeyDown={onKey(2)}
        data-testid="triple-scrubber-2"
        aria-label="Drag to compare"
        animate={
          !interacted && dragHandle.current !== 2
            ? { scale: [1, 1.1, 1] }
            : { scale: 1 }
        }
        transition={{
          duration: 1.4,
          repeat: !interacted ? Infinity : 0,
          ease: "easeInOut",
          delay: 0.3,
        }}
        className={`absolute top-1/2 -translate-y-1/2 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white flex items-center justify-center ${
          dragHandle.current === 2 ? "shadow-xl" : "shadow-lg hover:scale-105"
        }`}
        style={{
          left: `${pos2}%`,
          x: "-50%",
          y: "-50%",
          cursor: dragging ? "grabbing" : "grab",
          border: "3px solid #189A96",
        }}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 100 100"
          fill="none"
          animate={interacted ? { x: 0 } : { x: [0, -5, 0, 5, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
        >
          <polygon points="0 50 37 68 37 32" fill="#189A96" />
          <polygon points="100 50 63 32 63 68" fill="#189A96" />
        </motion.svg>
      </motion.button>
    </div>
  );
}
